import React, { useRef, useEffect } from "react";
import { api, formatEUR, formatDate } from "@/lib/api";
import { sendFiscalVoid, sendFiscalRefund, fiscalErrorMessage } from "@/lib/fiscal";
import { X, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { printReceiptViaQz, thermalPrintError } from "@/lib/printService";
import { StyledReceiptLines } from "@/components/PrecontoLinesEditor";
import { layoutOf, spacingOf, lineTotal } from "@/lib/escpos";

const paymentLabel = (k) => ({
  cash: "Contanti", card: "Carta", ticket: "Ticket", mixed: "Multiplo",
  assegno: "Assegno", bonifico: "Bonifico", non_riscosso: "Non riscosso", omaggio: "Omaggio",
}[k] || k || "Altro");
const SIZE_PX = { small: 9, normal: 11, large: 14 };
const alignClass = (a) => (a === "center" ? "text-center" : a === "right" ? "text-right" : "");

export default function ScontrinoPreview({ order, tenant, fiscal = true, autoPrint = false, onClose }) {
  const receiptRef = useRef(null);
  // Stampa automatica senza finestra di conferma (usata per il Preconto): stampa e torna alla cassa
  useEffect(() => {
    if (autoPrint && order) {
      (async () => {
        try { await printReceipt(); } catch { /* noop */ }
        onClose?.();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!order) return null;
  const date = formatDate(order.closed_at || order.created_at);
  const loyaltyUrl = order.customer_id && tenant?.id
    ? `${window.location.origin}/carta/${tenant.id}/${order.customer_id}`
    : null;
  // Movimento gestionale: documento come il preconto ma SENZA la scritta PRECONTO
  const isManagement = order?.receipt_type === "management";
  const isPreconto = !fiscal && !isManagement && order?.receipt_type !== "non_fiscal";
  const isRefund = order?.order_type === "refund";
  const isAnnullo = isRefund && order?.refund_reason === "annullamento";
  // Riferimento chiusura Z: 0092-0028 con stampante fiscale attiva, solo progressivo altrimenti
  const pad4 = (n) => String(n).padStart(4, "0");
  const docRef = (n) => (order.z_number != null ? `${pad4(order.z_number)}-${pad4(n)}` : pad4(n));
  const usePrecontoBrand = isPreconto || isManagement;
  // Intestazione personalizzata preconto/gestionale — se presente SOSTITUISCE i dati aziendali
  const customHeaderLines = usePrecontoBrand && Array.isArray(tenant?.preconto_header_lines) && tenant.preconto_header_lines.length
    ? tenant.preconto_header_lines : null;
  const customHeaderText = usePrecontoBrand && !customHeaderLines && tenant?.preconto_header ? tenant.preconto_header : null;
  const hasCustomHeader = !!(customHeaderLines || customHeaderText);
  // Layout configurabile (posizione + grandezza) per data/articoli/operatore
  const dateLay = layoutOf(tenant, "receipt", "date");
  const itemsLay = layoutOf(tenant, "receipt", "items");
  const opLay = layoutOf(tenant, "receipt", "operator");
  // Spaziatura configurabile (righe vuote tra le sezioni) + sconti
  const sp = spacingOf(tenant, "receipt");
  const gap = (n) => (n > 0 ? <div style={{ height: n * 14 }} /> : null);
  const grossSum = (Array.isArray(order.lines) ? order.lines : []).reduce(
    (s, l) => s + lineTotal(l) + (l.extras || []).reduce((a, ex) => a + Number(ex.price || 0), 0), 0);
  const orderPct = Number(order.discount_pct || 0);
  const orderEur = Number(order.discount_eur || 0);
  // Config comanda per applicare stile note (grassetto/upper/large)
  const cfg = tenant?.comanda_config || {};
  const noteBold = cfg.notes_bold !== false; // default true
  const noteUpper = cfg.notes_uppercase !== false; // default true
  const noteLarge = cfg.notes_size === "large";
  const noteStyle = {
    fontWeight: noteBold ? 700 : 400,
    textTransform: noteUpper ? "uppercase" : "none",
    fontSize: noteLarge ? "13px" : "11px",
    letterSpacing: noteUpper ? "0.02em" : 0,
  };

  // Annullo / storno: il tasto Stampa manda il comando alla RT, non una copia termica.
  const printReceipt = async () => {
    if (fiscal && isRefund) {
      try {
        let target = order;
        if (order.original_order_id) {
          const got = await api.get(`/orders/${order.original_order_id}`);
          target = got.data || order;
        }
        const res = (isAnnullo || target.refund_full)
          ? await sendFiscalVoid({ order: target })
          : await sendFiscalRefund({ order });
        if (res?.ok) {
          toast.success(`Inviato alla fiscale "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
          return;
        }
        toast.error(`${fiscalErrorMessage(res || {})}${res?.voidRef ? ` — ${res.voidRef}` : ""}`, { duration: 12000 });
      } catch (e) {
        toast.error(String(e?.message || e), { duration: 8000 });
      }
      return;
    }
    const kind = fiscal ? "fiscal_test" : isManagement ? "management" : isPreconto ? "preconto" : "non_fiscal";
    try {
      const res = await printReceiptViaQz({ tenant, order, kind });
      if (res.ok) {
        toast.success(`Stampato su "${res.printer}"${res.via === "android" ? " (tablet LAN)" : " (QZ Tray)"}`);
        return;
      }
      toast.error(thermalPrintError(res), { duration: 9000 });
    } catch (e) {
      toast.error(String(e?.message || e), { duration: 8000 });
    }
  };

  return (
    <div className={autoPrint ? "fixed inset-0 opacity-0 pointer-events-none overflow-hidden" : "fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"} onClick={autoPrint ? undefined : onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-[#1E2D5B]" />
            <h3 className="font-heading text-lg font-semibold">
              {fiscal && isRefund
                ? (isAnnullo ? "Documento di Annullamento" : "Scontrino di Storno")
                : fiscal ? "Scontrino Fiscale" : isManagement ? "Movimento Gestionale" : "Scontrino NON Fiscale"}
            </h3>
          </div>
          <button data-testid="scontrino-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {order.cash_tendered != null && order.change_due != null && (
          <div data-testid="resto-banner" className="mb-3 rounded-md bg-[#15803D] text-white px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-widest">Resto da dare</span>
            <span className="font-mono font-bold text-3xl tabular-nums">{formatEUR(order.change_due)}</span>
          </div>
        )}

        <div ref={receiptRef} className="bg-velta-bg border border-dashed border-velta-border rounded-md p-5 font-mono text-xs">
          {customHeaderLines ? (
            <div className="mb-2"><StyledReceiptLines lines={customHeaderLines} testid="preconto-header-styled" /></div>
          ) : customHeaderText ? (
            <div className="text-center font-bold text-[11px] mb-2 whitespace-pre-line">{customHeaderText}</div>
          ) : null}
          {!hasCustomHeader && (
            <>
              {!usePrecontoBrand && tenant?.receipt_header && (
                <div className="text-center font-bold text-[11px] mb-2 whitespace-pre-line">{tenant.receipt_header}</div>
              )}
              <div className="text-center mb-3">
                {tenant?.logo_data_url && (
                  <img src={tenant.logo_data_url} alt="logo" className="mx-auto mb-2 max-h-16 object-contain" />
                )}
                <div className="font-bold text-sm">{tenant?.business_name || "VELTA SISTEMI"}</div>
                <div className="text-velta-mute">{tenant?.address}</div>
                <div className="text-velta-mute">P.IVA {tenant?.vat_number || "—"}</div>
                {tenant?.phone && <div className="text-velta-mute">Tel. {tenant.phone}</div>}
              </div>
            </>
          )}
          {gap(sp.after_header)}
          <div className="border-t border-dashed border-velta-border my-2"></div>
          {fiscal && order.receipt_number && (
            <div className="flex justify-between mb-1">
              <span>Scontrino N.</span><span className="font-bold">#{String(order.receipt_number).padStart(4, "0")}</span>
            </div>
          )}
          {isRefund && (
            <div className="mb-1" data-testid="scontrino-refund-ref">
              <div className="flex justify-between">
                <span>{isAnnullo ? "Annullo N." : "Storno N."}</span>
                <span className="font-bold">S-{order.refund_number || "—"}</span>
              </div>
              {order.original_receipt_number && (
                <div className="flex justify-between text-velta-mute">
                  <span>Rif. scontrino</span><span>#{String(order.original_receipt_number).padStart(4, "0")}</span>
                </div>
              )}
            </div>
          )}
          {!fiscal && order.order_number && (
            <div className="text-center my-3">
              <div className="text-velta-mute">N. ORDINE</div>
              <div className="font-heading font-bold text-5xl text-[#1E2D5B]">{order.order_number}</div>
            </div>
          )}
          {!usePrecontoBrand && (dateLay.align === "left" ? (
            <div className="flex justify-between mb-3" style={{ fontSize: SIZE_PX[dateLay.size] }} data-testid="scontrino-date">
              <span>Data</span><span>{date}</span>
            </div>
          ) : (
            <div className={`mb-3 ${alignClass(dateLay.align)}`} style={{ fontSize: SIZE_PX[dateLay.size] }} data-testid="scontrino-date">
              Data {date}
            </div>
          ))}
          {gap(sp.after_date)}
          <div className="border-t border-dashed border-velta-border my-2"></div>
          {(Array.isArray(order.lines) ? order.lines : []).map((l, i) => (
            <div key={i} className="mb-1">
              {itemsLay.align === "left" ? (
                <div className="flex justify-between" style={{ fontSize: SIZE_PX[itemsLay.size] }}>
                  <span className="truncate flex-1 mr-2">{l.qty}x {l.name}</span>
                  <span>{formatEUR(l.price * l.qty)}</span>
                </div>
              ) : (
                <div className={alignClass(itemsLay.align)} style={{ fontSize: SIZE_PX[itemsLay.size] }}>
                  {l.qty}x {l.name} {formatEUR(l.price * l.qty)}
                </div>
              )}
              {lineTotal(l) < l.price * l.qty - 0.001 && (
                <div className="flex justify-between text-[#0F766E]" data-testid={`scontrino-line-disc-${i}`}>
                  <span className="pl-3">Sconto{l.discount_pct ? ` -${l.discount_pct}%` : ""}</span>
                  <span>-{formatEUR(l.price * l.qty - lineTotal(l))}</span>
                </div>
              )}
              {(Array.isArray(l.extras) ? l.extras : []).map((ex, j) => (
                <div key={j} className="flex justify-between text-velta-mute pl-3">
                  <span>+ {ex.name}</span><span>{ex.price ? formatEUR(ex.price) : "gratis"}</span>
                </div>
              ))}
              {l.note && (
                <div className="pl-3" style={noteStyle} data-testid={`scontrino-note-${i}`}>
                  ↳ {l.note}
                </div>
              )}
            </div>
          ))}
          {order.notes && (
            <div className="mt-3 pt-2 border-t border-dashed border-velta-border" data-testid="scontrino-table-note">
              <div className="text-[10px] text-velta-mute uppercase tracking-widest">Nota tavolo</div>
              <div style={{ ...noteStyle, marginTop: 2 }}>{order.notes}</div>
            </div>
          )}
          {gap(sp.after_items)}
          <div className="border-t border-dashed border-velta-border my-2"></div>
          {(orderPct > 0 || orderEur > 0) && (
            <div className="mt-1" data-testid="scontrino-order-discounts">
              <div className="flex justify-between"><span>Subtotale</span><span>{formatEUR(grossSum)}</span></div>
              {orderPct > 0 && (
                <div className="flex justify-between text-[#2F9E77]"><span>Sconto -{orderPct}%</span><span>-{formatEUR(grossSum * orderPct / 100)}</span></div>
              )}
              {orderEur > 0 && (
                <div className="flex justify-between text-[#0F766E]"><span>Sconto</span><span>-{formatEUR(Math.min(orderEur, grossSum * (1 - orderPct / 100)))}</span></div>
              )}
            </div>
          )}
          <div className="flex justify-between font-bold text-base mt-2">
            <span>TOTALE</span><span>{formatEUR(order.total)}</span>
          </div>
          {fiscal && order.vat_break && (
            <div className="mt-2 text-velta-mute text-[10px]">
              {Object.entries(order.vat_break).map(([rate, amount]) => (
                <div key={rate} className="flex justify-between">
                  <span>IVA {rate}% (scorporo)</span>
                  <span>{formatEUR(parseFloat(amount) - parseFloat(amount) / (1 + parseFloat(rate) / 100))}</span>
                </div>
              ))}
            </div>
          )}
          {order.payment_method && (
            <div className="mt-3 pt-2 border-t border-dashed border-velta-border">
              <div className="flex justify-between"><span>Pagamento</span><span className="uppercase">{paymentLabel(order.payment_method)}</span></div>
              {order.payment_breakdown && Object.entries(order.payment_breakdown).filter(([, v]) => Number(v) > 0).map(([k, v]) => (
                <div key={k} className="flex justify-between text-velta-mute pl-3" data-testid={`scontrino-pay-${k}`}>
                  <span>{paymentLabel(k)}</span><span>{formatEUR(v)}</span>
                </div>
              ))}
            </div>
          )}
          {order.cash_tendered != null && (
            <div className="mt-2" data-testid="scontrino-resto">
              <div className="flex justify-between"><span>Contanti ricevuti</span><span>{formatEUR(order.cash_tendered)}</span></div>
              {order.change_due != null && (
                <div className="flex justify-between font-bold text-base mt-1"><span>RESTO</span><span>{formatEUR(order.change_due)}</span></div>
              )}
            </div>
          )}
          {order.lottery_code && (
            <div className="mt-2 text-velta-mute text-[10px]">Lotteria: <span className="font-bold text-velta-ink tracking-widest">{order.lottery_code}</span></div>
          )}
          {order.loyalty_redeem_discount > 0 && (
            <div className="mt-2 pt-2 border-t border-dashed border-velta-border">
              <div className="flex justify-between text-[#2F9E77] font-bold">
                <span>Sconto Fedeltà</span><span>-{formatEUR(order.loyalty_redeem_discount)}</span>
              </div>
              <div className="text-[10px] text-velta-mute">({order.loyalty_redeemed_points} punti utilizzati)</div>
            </div>
          )}
          {loyaltyUrl && (
            <div className="mt-3 pt-3 border-t border-dashed border-velta-border text-center" data-testid="scontrino-loyalty-qr">
              <div className="text-[9px] text-velta-mute uppercase tracking-widest mb-2">Saldo punti & premi</div>
              <div className="bg-white inline-block p-2 rounded">
                <QRCodeSVG value={loyaltyUrl} size={88} includeMargin={false} level="M" />
              </div>
              <div className="text-[9px] text-velta-mute mt-1">Inquadra con lo smartphone</div>
            </div>
          )}
          {order.operator_name && (
            <div className={`mt-2 text-velta-mute ${alignClass(opLay.align)}`} style={{ fontSize: SIZE_PX[opLay.size] }} data-testid="scontrino-operator">
              Operatore: {order.operator_name}
            </div>
          )}
          {tenant?.receipt_promo && (
            <div className="mt-3 pt-2 border-t border-dashed border-velta-border text-center font-bold text-[11px] text-[#1E2D5B] whitespace-pre-line">
              {tenant.receipt_promo}
            </div>
          )}
          {tenant?.receipt_thank_you && (
            <div className="mt-2 text-center font-bold whitespace-pre-line">{tenant.receipt_thank_you}</div>
          )}
          {gap(sp.after_total)}
          {usePrecontoBrand && (
            <div className={`mt-2 text-velta-mute ${alignClass(dateLay.align)}`} style={{ fontSize: SIZE_PX[dateLay.size] }} data-testid="scontrino-emesso">
              {isManagement && order.management_number != null && (
                <div data-testid="scontrino-doc-nr">DOC.GESTIONALE NR. {docRef(order.management_number)}</div>
              )}
              {isPreconto && order.preconto_number != null && (
                <div data-testid="scontrino-doc-nr">DOC. NR. {docRef(order.preconto_number)}</div>
              )}
              <div>Emesso il {(() => { const d = new Date(order.closed_at || Date.now()); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; })()}</div>
            </div>
          )}
          <div className="border-t border-dashed border-velta-border my-3"></div>
          <div className="text-center text-velta-mute text-[10px]" data-testid="scontrino-doc-type">
            {fiscal && isRefund
              ? (isAnnullo ? "DOCUMENTO COMMERCIALE EMESSO PER ANNULLAMENTO" : "DOCUMENTO COMMERCIALE EMESSO PER RESO MERCE")
              : fiscal
              ? "DOCUMENTO COMMERCIALE DI VENDITA O PRESTAZIONE"
              : isManagement
              ? "DOCUMENTO NON FISCALE"
              : isPreconto
              ? "PRECONTO — Non valido ai fini fiscali"
              : "DOCUMENTO NON FISCALE"}
            {usePrecontoBrand && Array.isArray(tenant?.preconto_footer_lines) && tenant.preconto_footer_lines.length ? (
              <div className="mt-1 text-velta-ink"><StyledReceiptLines lines={tenant.preconto_footer_lines} testid="preconto-footer-styled" /></div>
            ) : usePrecontoBrand && tenant?.preconto_footer ? (
              <><br /><span className="block mt-1 whitespace-pre-line">{tenant.preconto_footer}</span></>
            ) : tenant?.receipt_footer && (
              <><br /><span className="block mt-1 whitespace-pre-line">{tenant.receipt_footer}</span></>
            )}
            <br />Powered by VELTA SISTEMI
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            data-testid="scontrino-print-btn"
            onClick={printReceipt}
            className="h-12 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md font-medium btn-tactile flex items-center justify-center gap-2"
          >
            <Printer className="w-4 h-4" /> Stampa
          </button>
          <button data-testid="scontrino-ok" onClick={onClose} className="h-12 bg-velta-ink text-white rounded-md font-medium btn-tactile">OK</button>
        </div>
      </div>
    </div>
  );
}
